import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../store';
import {
  useDeleteData,
  useFetchData,
  useReactivateData,
  useUserStore,
} from '../../hooks';
import React, { useState } from 'react';
import { PageableResponse } from '../../types';
import { Resource } from '../../lib/enums';
import { ModuleAccess, User } from '../../types/auth.model';
import { convertDate, dateTimeFormat } from '../../lib/utils';
import { Button, Popconfirm, Segmented, Space, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  CheckCircleOutlined,
  EditOutlined,
  KeyOutlined,
  StopOutlined,
} from '@ant-design/icons';
import ChangePasswordModal from '../../components/ChangePasswordModal';

const UserTable = ({
  api,
  moduleName,
  userModuleAccess,
}: {
  api: string;
  moduleName: string;
  userModuleAccess: ModuleAccess | undefined;
}) => {
  const [searchParams] = useSearchParams();
  const userStore = useUserStore();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  // Active vs Inactive (deactivated) view. Deactivated users cannot log in.
  const [showActive, setShowActive] = useState<boolean>(true);
  const [pwUserId, setPwUserId] = useState<string>('');
  const [isPwModalOpen, setPwModalOpen] = useState<boolean>(false);

  const isAdmin = user?.role === 'ROLE_ADMIN';
  const canEdit = userModuleAccess?.write || isAdmin;
  const canManageStatus = userModuleAccess?.delete || isAdmin;

  const { data, isLoading } = useFetchData<PageableResponse<User>>(api, {
    resource: searchParams.get('resource') || Resource.MIN,
    search: searchParams.get('search') || '',
    active: showActive,
    length: parseInt(searchParams.get('length') || '999999'),
  });

  const { mutate: deactivate, isPending: isDeactivating } = useDeleteData(api);
  const { mutate: reactivate, isPending: isReactivating } =
    useReactivateData(api);

  const handleDataEdit = (record: User) => {
    userStore.setData(record);
    navigate(`/user/${record.id}`);
  };

  const handleChangePassword = (record: User) => {
    setPwUserId(record.id);
    setPwModalOpen(true);
  };

  const columns: ColumnsType<User> = [
    { title: 'Username', dataIndex: 'username', width: 150 },
    {
      title: 'Name',
      dataIndex: 'firstName',
      render: (_text: string, record: User) => (
        <span>
          {[record.firstName, record.lastName].filter(Boolean).join(' ')}
        </span>
      ),
    },
    { title: 'Email', dataIndex: 'email' },
    {
      title: 'Role',
      dataIndex: 'role',
      width: 130,
      render: (_text: string, record: User) => (
        <span>{record.role?.replace('ROLE_', '')}</span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'active',
      width: 110,
      render: (_text: boolean, record: User) => {
        const active = record.active ?? showActive;
        return (
          <Tag color={active ? 'green' : 'red'} bordered={false}>
            {active ? 'Active' : 'Inactive'}
          </Tag>
        );
      },
    },
    {
      title: 'Created By/Date',
      dataIndex: 'createdBy',
      render: (_text: string, record: User) => (
        <div className="flex flex-col">
          <span>{record.createdBy}</span>
          {record.timeCreated != null && (
            <span className="text-xs text-gray-400">
              {convertDate(record.timeCreated, dateTimeFormat)}
            </span>
          )}
        </div>
      ),
    },
    {
      title: 'Updated By/Date',
      dataIndex: 'updatedBy',
      render: (_text: string, record: User) => (
        <div className="flex flex-col">
          <span>{record.updatedBy}</span>
          {record.timeUpdated != null && (
            <span className="text-xs text-gray-400">
              {convertDate(record.timeUpdated, dateTimeFormat)}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'action',
      title: 'Action',
      width: 160,
      render: (_text: string, record: User) => (
        <Space size={4}>
          {canEdit && (
            <Tooltip title="Edit">
              <Button
                type="text"
                icon={<EditOutlined style={{ color: '#4080fd' }} />}
                onClick={() => handleDataEdit(record)}
              />
            </Tooltip>
          )}
          {isAdmin && (
            <Tooltip title="Change Password">
              <Button
                type="text"
                icon={<KeyOutlined />}
                onClick={() => handleChangePassword(record)}
              />
            </Tooltip>
          )}
          {canManageStatus &&
            (showActive ? (
              <Popconfirm
                title="Deactivate this user?"
                description="They will no longer be able to log in."
                okText="Deactivate"
                okButtonProps={{ danger: true }}
                cancelText="Cancel"
                onConfirm={() => deactivate({ id: record.id })}
              >
                <Tooltip title="Deactivate">
                  <Button type="text" danger icon={<StopOutlined />} />
                </Tooltip>
              </Popconfirm>
            ) : (
              <Popconfirm
                title="Activate this user?"
                description="They will be able to log in again."
                okText="Activate"
                cancelText="Cancel"
                onConfirm={() => reactivate({ id: record.id })}
              >
                <Tooltip title="Activate">
                  <Button
                    type="text"
                    icon={<CheckCircleOutlined style={{ color: '#389e0d' }} />}
                  />
                </Tooltip>
              </Popconfirm>
            ))}
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="px-4 pb-3">
        <Segmented
          value={showActive ? 'active' : 'inactive'}
          onChange={(val) => setShowActive(val === 'active')}
          options={[
            { label: 'Active', value: 'active' },
            { label: 'Inactive', value: 'inactive' },
          ]}
        />
      </div>
      <div className="flex flex-col h-[calc(74vh)] overflow-auto px-4">
        <Table<User>
          rowKey={(record) => record.id}
          dataSource={data?.data || []}
          loading={isLoading || isDeactivating || isReactivating}
          columns={columns}
          size="middle"
          pagination={{
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `${total} ${moduleName.toLowerCase()}(s)`,
          }}
        />
      </div>

      {pwUserId !== '' && (
        <ChangePasswordModal
          userId={pwUserId}
          visible={isPwModalOpen}
          onClose={() => setPwModalOpen(false)}
        />
      )}
    </>
  );
};

export default UserTable;
